import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Box,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  Checkbox,
  Typography,
  CircularProgress,
  Grid,
  MenuItem,
  Menu,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Edit,
  Delete,
  Print,
  Visibility,
  Add,
  Close,
  AttachFile,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import "react-toastify/dist/ReactToastify.css";
import { Chip } from "@mui/material";
import {
  GeneralVoucherList,
  GeneralVoucherDelete,
  ApproveGeneralVoucher,
  GeneralVoucherExportApi,
} from "../../../Services/Services";
import { generatePdf } from "./GeneralVoucherPrint";
import InfiniteScroll from "react-infinite-scroll-component";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Description as FileIcon,
} from "@mui/icons-material";
import { Collapse } from "@mui/material";
import dayjs from "dayjs";

const GeneralVoucher = () => {
  // ========== State Definitions ==========
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const [hasMore, setHasMore] = useState(true);
  const [generalVouchers, setGeneralVouchers] = useState([]);
  const [rows, setRows] = useState([]);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [GeneralToApprove, setGeneralToApprove] = useState(null);
const [loading, setLoading] = useState(false); // For scroll pagination
const [filterLoading, setFilterLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedVoucher, setExpandedVoucher] = useState(null);

  // 🆕 Date Range States
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [selectingDate, setSelectingDate] = useState(null);
  const dateInputRef = useRef(null);

  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [billDateMenuAnchor, setBillDateMenuAnchor] = useState(null);
  const [billDateRange, setBillDateRange] = useState({ from: "", to: "" });
  const [tempBillDateRange, setTempBillDateRange] = useState({
    from: "",
    to: "",
  });
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  // ✅ Updated filters
  const [filters, setFilters] = useState({
    date_range: "today", // 🆕 Added
    billNoSearch: "",
    ledgerSearch: "",
    projectSearch: "",
    bill_date_from: "",
    bill_date_to: "",
  });

  const [remarkInput, setRemarkInput] = useState("");
  const [showRemarkForGeneralId, setShowRemarkForGeneralId] = useState(null);
  const [actionType, setActionType] = useState(null);
  const userRole = localStorage.getItem("role");
  const [isExcelLoading, setExcelLoading] = useState(false);

  // Add this function before the return statement
  const handleExportGeneralVoucher = async () => {
    if (generalVouchers.length === 0) {
      toast.warning("No data available to export");
      return;
    }

    setExcelLoading(true);
    try {
      // ✅ Build params exactly like fetchGeneralVouchers
      const params = { ...filters };

      // ✅ Add custom dates if custom range selected
      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }

      // ✅ Add bill date range
      if (filters.bill_date_from && filters.bill_date_to) {
        params.bill_date_from = filters.bill_date_from;
        params.bill_date_to = filters.bill_date_to;
      }

      console.log("Export Params:", params);

      const blob = await GeneralVoucherExportApi(params);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `General_Vouchers_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  // 🆕 Helper function to format date display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // 🆕 Get date range display text
  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  // ✅ Updated useEffect
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchGeneralVouchers(1, true);
    }, 500);

    return () => clearTimeout(handler);
  }, [filters, customDateRange, billDateRange]);

  const fetchGeneralVouchers = async (pageNum = 1, reset = false) => {
  try {
    // ✅ Use filterLoading for reset/filter changes, loading for scroll
    if (reset) {
      setFilterLoading(true);
    } else {
      setLoading(true);
    }

    // 🆕 Build params with custom date handling
    const params = { ...filters };

    // Add custom dates if custom range selected
    if (
      filters.date_range === "custom" &&
      customDateRange.from &&
      customDateRange.to
    ) {
      params.from_date = customDateRange.from;
      params.to_date = customDateRange.to;
    }

    if (filters.bill_date_from && filters.bill_date_to) {
      params.bill_date_from = filters.bill_date_from;
      params.bill_date_to = filters.bill_date_to;
    }
    console.log("API Params being sent:", params);

    const data = await GeneralVoucherList(pageNum, params);
    
    if (reset || pageNum === 1) {
      setGeneralVouchers(
        Array.isArray(data) ? data.results : data.results || []
      );
    } else {
      setGeneralVouchers((prev) => [
        ...prev,
        ...(Array.isArray(data) ? data.results : data.results || []),
      ]);
    }
    setHasMore(Array.isArray(data) ? data.next !== null : data.next !== null);
    setPage(pageNum);
  } catch (error) {
    console.error("Failed to fetch vouchers:", error);
    toast.error("Failed to load vouchers");
  } finally {
    // ✅ Reset the appropriate loading state
    setFilterLoading(false);
    setLoading(false);
  }
};

  // ✅ Updated fetchMoreData
  const fetchMoreData = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchGeneralVouchers(nextPage, false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Updated handleReset
  const handleReset = () => {
    setFilters({
      date_range: "today", // 🆕 Reset to today
      billNoSearch: "",
      projectSearch: "",
      ledgerSearch: "",
      bill_date_from: "",
      bill_date_to: "",
    });
    setCustomDateRange({ from: "", to: "" }); // 🆕 Reset custom dates
    setSelectingDate(null); // 🆕
    setGeneralVouchers([]);
    setPage(1);
    setHasMore(true);
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
    setBillDateRange({ from: "", to: "" });
    setTempBillDateRange({ from: "", to: "" });
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  function blankRow() {
    return {
      gen_voucher_no: "",
      gen_number: "",
      supplier: "",
      expense: "",
      imprest: "",
      bill_no: "",
      bill_date: "",
      project: "",
      division: "",
      division_name: "",
      status: "",
      payment_status: "",
      amount: "",
      attachment: "",
      narration: "",
      cost_type: "",
      created_at: localStorage.getItem("user_id"),
    };
  }

  console.log(generalVouchers);

  const handleApprove = async (voucher) => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveGeneralVoucher(
        voucher.gen_voucher_no,
        approvedById,
        voucher.status,
        remarkInput
      );
      setGeneralVouchers((prev) =>
        prev.map((item) =>
          item.gen_voucher_no === voucher.gen_voucher_no ? updated : item
        )
      );

      toast.success(
        updated.status === "Approved"
          ? "General voucher approved successfully!"
          : "General voucher unapproved successfully!"
      );
    } catch (error) {
      console.error("Approval Error:", error);
      toast.error("Failed to approve General Voucher.");
    }
  };

  const userId = Number(localStorage.getItem("user_id"));

  const handleEditVoucher = async (voucher, index) => {
    try {
      navigate("/dashboard/general-voucher-create", {
        state: {
          editMode: true,
          voucherData: {
            ...voucher,
            gen_voucher_no: voucher.gen_voucher_no,
            gen_number: voucher.gen_number,
            imprest: voucher.imprest,
            supplier: voucher.supplier,
            project: voucher.project,
            payment_status: voucher.payment_status,
            attachment: voucher.attachment,
            narration: voucher.narration,
            grandtotal: voucher.grandtotal,
            sub_vouchers: voucher.sub_vouchers || [],
            expense_rows: voucher.expense_rows || [],
          },
        },
      });
    } catch (error) {
      console.error("Error navigating to edit:", error);
      toast.error("Failed to load voucher for editing");
    }
  };

  const handleDeleteVoucher = async (gen_voucher_no) => {
    const success = await GeneralVoucherDelete(gen_voucher_no);

    if (success) {
      const updated = generalVouchers.filter(
        (voucher) => voucher.gen_voucher_no !== gen_voucher_no
      );
      setGeneralVouchers(updated);
      toast.success("Voucher deleted successfully");
    } else {
      toast.error("Failed to delete voucher");
    }
  };

  // Handle Apply
  const handleApplyBillDateRange = () => {
    setBillDateRange(tempBillDateRange);
    setFilters((prev) => ({
      ...prev,
      bill_date_from: tempBillDateRange.from,
      bill_date_to: tempBillDateRange.to,
      date_range: "all",
    }));
    setBillDateMenuAnchor(null);
  };

  // Handle Cancel
  const handleCancelBillDateRange = () => {
    setTempBillDateRange(billDateRange);
    setBillDateMenuAnchor(null);
  };

  // Handle Clear
  const handleClearBillDateRange = () => {
    setBillDateRange({ from: "", to: "" });
    setTempBillDateRange({ from: "", to: "" });
    setFilters((prev) => ({
      ...prev,
      bill_date_from: "",
      bill_date_to: "",
    }));
    setBillDateMenuAnchor(null);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);
  // ✅ ADD THIS ENTIRE COMPONENT BEFORE return (
  const MobileVoucherCard = ({ voucher, index }) => {
    const isExpanded = expandedVoucher === voucher.gen_voucher_no;

    return (
      <Box key={voucher.gen_voucher_no}>
        {/* Main Row */}
        <Box
          onClick={() =>
            setExpandedVoucher(
              expandedVoucher === voucher.gen_voucher_no
                ? null
                : voucher.gen_voucher_no
            )
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": {
              bgcolor: "#e3f2fd",
            },
            borderLeft: `4px solid ${
              voucher.status === "Approved"
                ? "#4caf50"
                : voucher.status === "Rejected"
                ? "#f44336"
                : "#ff9800"
            }`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            {/* Voucher Number & Status */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography
                sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}
              >
                #{index + 1}
              </Typography>
              <Typography
                sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}
              >
                {voucher.gen_number}
              </Typography>
            </Box>

            {/* Ledger & Date */}
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {(
                  voucher.imprest_name ||
                  voucher.supplier_name ||
                  voucher.advanceworker_name ||
                  voucher.Transporter_name
                )?.substring(0, 20) || "N/A"}
              </span>
              <span style={{ color: "#999" }}>•</span>
              <span style={{ fontSize: "0.7rem" }}>
                {formatDate(voucher.bill_date)}
              </span>
            </Typography>
          </Box>

          {/* Expand Icon */}
          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        {/* Expanded Details */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: `4px solid ${
                voucher.status === "Approved"
                  ? "#4caf50"
                  : voucher.status === "Rejected"
                  ? "#f44336"
                  : "#ff9800"
              }`,
            }}
          >
            {/* 2 Column Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.8,
                mb: 1,
              }}
            >
              {/* Status */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  STATUS
                </Typography>
                <Chip
                  label={voucher.status}
                  color={
                    voucher.status === "Approved"
                      ? "success"
                      : voucher.status === "Rejected"
                      ? "error"
                      : "warning"
                  }
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                />
              </Box>

              {/* Payment Status */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  PAYMENT
                </Typography>
                <Chip
                  label={voucher.payment_status}
                  color={
                    voucher.payment_status === "Paid"
                      ? "success"
                      : voucher.payment_status === "Unpaid"
                      ? "error"
                      : "warning"
                  }
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                />
              </Box>

              {/* Bill No */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  BILL NO
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                >
                  {voucher.bill_no}
                </Typography>
              </Box>

              {/* Division */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  DIVISION
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                >
                  {voucher.division_name}
                </Typography>
              </Box>

              {/* Total Amount */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  TOTAL AMT
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#2e7d32",
                  }}
                >
                  ₹ {Number(voucher.grandtotal || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>

              {/* Paid Amount */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  PAID AMT
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#1976d2",
                  }}
                >
                  ₹{" "}
                  {Number(voucher.receipt_amount || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>

              {/* Balance Amount */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  BALANCE
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#d32f2f",
                  }}
                >
                  ₹{" "}
                  {Number(voucher.balance_amount || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>

              {/* Created By */}
              {voucher.created_by_name && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "#999",
                      letterSpacing: "0.5px",
                    }}
                  >
                    CREATED BY
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                  >
                    {voucher.created_by_name}
                  </Typography>
                </Box>
              )}

              {/* Approved By */}
              {voucher.approved_name && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "#999",
                      letterSpacing: "0.5px",
                    }}
                  >
                    APPROVED BY
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                  >
                    {voucher.approved_name}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Project - Full Width */}
            {voucher.project_name && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  PROJECT
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#555",
                    lineHeight: 1.3,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {voucher.project_name}
                </Typography>
              </Box>
            )}

            {/* Narration */}
            {voucher.narration && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  NARRATION
                </Typography>
                <Typography
                  sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3 }}
                >
                  {voucher.narration}
                </Typography>
              </Box>
            )}

            {/* Remarks */}
            {voucher.remarks && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  REMARKS
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#555",
                    fontStyle: "italic",
                    lineHeight: 1.3,
                  }}
                >
                  {voucher.remarks}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* Approve - Admin Only */}
              {userRole != "Staff" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    bgcolor:
                      voucher.status === "Approved"
                        ? "#e8f5e9"
                        : voucher.status === "Rejected"
                        ? "#ffebee"
                        : "#fff3e0",
                    px: 1,
                    py: 0.5,
                    borderRadius: "6px",
                    border: `1px solid ${
                      voucher.status === "Approved"
                        ? "#4caf50"
                        : voucher.status === "Rejected"
                        ? "#f44336"
                        : "#ff9800"
                    }`,
                  }}
                >
                  <Checkbox
                    checked={
                      voucher.status === "Approved" ||
                      voucher.status === "Rejected"
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      setApproveAnchorEl(e.currentTarget);
                      setGeneralToApprove(voucher);
                    }}
                    sx={{
                      padding: 0,
                      transform: "scale(0.6)",
                      color:
                        voucher.status === "Approved"
                          ? "#4caf50"
                          : voucher.status === "Rejected"
                          ? "#f44336"
                          : "#ff9800",
                      "&.Mui-checked": {
                        color:
                          voucher.status === "Approved" ? "#4caf50" : "#f44336",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color:
                        voucher.status === "Approved"
                          ? "#2e7d32"
                          : voucher.status === "Rejected"
                          ? "#c62828"
                          : "#f57c00",
                    }}
                  >
                    {voucher.status === "Approved"
                      ? "Approved"
                      : voucher.status === "Rejected"
                      ? "Rejected"
                      : "Approve"}
                  </Typography>
                </Box>
              )}

              {/* Action Icons */}
              <Box sx={{ display: "flex", gap: 0.6 }}>
                {/* View Attachment */}
                {typeof voucher.attachment === "string" && (
                  <IconButton
                    size="small"
                    component="a"
                    href={voucher.attachment}
                    target="_blank"
                    sx={{
                      bgcolor: "#e3f2fd",
                      borderRadius: "6px",
                      width: 32,
                      height: 32,
                      "&:hover": {
                        bgcolor: "#bbdefb",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    <Visibility sx={{ fontSize: "1rem", color: "#1976d2" }} />
                  </IconButton>
                )}

                {/* Edit */}
                <IconButton
                  size="small"
                  disabled={voucher.status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditVoucher(voucher, index);
                  }}
                  sx={{
                    bgcolor:
                      voucher.status === "Approved" ? "#f5f5f5" : "#e8f5e9",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor:
                        voucher.status === "Approved" ? "#f5f5f5" : "#c8e6c9",
                      transform:
                        voucher.status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <Edit
                    sx={{
                      fontSize: "1rem",
                      color:
                        voucher.status === "Approved" ? "#bdbdbd" : "#4caf50",
                    }}
                  />
                </IconButton>

                {/* Delete */}
                <IconButton
                  size="small"
                  disabled={voucher.status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteAnchorEl(e.currentTarget);
                    setSelectedVoucherId(voucher.gen_voucher_no);
                  }}
                  sx={{
                    bgcolor:
                      voucher.status === "Approved" ? "#f5f5f5" : "#ffebee",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor:
                        voucher.status === "Approved" ? "#f5f5f5" : "#ffcdd2",
                      transform:
                        voucher.status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <Delete
                    sx={{
                      fontSize: "1rem",
                      color:
                        voucher.status === "Approved" ? "#bdbdbd" : "#f44336",
                    }}
                  />
                </IconButton>

                {/* Print */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    generatePdf(voucher);
                  }}
                  sx={{
                    bgcolor: "#fff3e0",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": { bgcolor: "#ffe0b2", transform: "scale(1.05)" },
                    transition: "all 0.2s",
                  }}
                >
                  <Print sx={{ fontSize: "1rem", color: "#ff9800" }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <>
      <ToastContainer />
      {/* 🆕 Updated Filter Section */}
      <Box sx={{ mt: -5, pt: -3, mb: 2, width: "100%" }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range Dropdown */}
          <Grid item xs={12} sm={1.8}>
            <TextField
              fullWidth
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                customDateRange.from &&
                customDateRange.to
                  ? `${formatDateDisplay(
                      customDateRange.from
                    )} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                  ? "Today"
                  : filters.date_range === "yesterday"
                  ? "Yesterday"
                  : filters.date_range === "this_week"
                  ? "This Week"
                  : filters.date_range === "previous_week"
                  ? "Previous Week"
                  : filters.date_range === "this_month"
                  ? "This Month"
                  : filters.date_range === "current_year"
                  ? "Current Year"
                  : filters.date_range === "financial_year"
                  ? "Financial Year"
                  : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                fontWeight: "bold",
                "& .MuiInputBase-input": {
                  fontSize: "0.8rem",
                  padding: "4px 8px",
                },
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.6rem",
                },
              }}
            />
          </Grid>

          {/* Bill No */}
          <Grid item xs={12} sm={1.8}>
            <TextField
              fullWidth
              label="Search by Bill No"
              variant="outlined"
              size="small"
              name="billNoSearch"
              value={filters.billNoSearch}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          {/* Project */}
          <Grid item xs={12} sm={1.8}>
            <TextField
              fullWidth
              label="Project"
              variant="outlined"
              size="small"
              name="projectSearch"
              value={filters.projectSearch}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          {/* Ledger */}
          <Grid item xs={12} sm={1.8}>
            <TextField
              fullWidth
              label="Ledger"
              variant="outlined"
              size="small"
              name="ledgerSearch"
              value={filters.ledgerSearch}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={1.8}>
            <TextField
              label="Bill Date"
              value={
                billDateRange.from && billDateRange.to
                  ? `${formatDateDisplay(
                      billDateRange.from
                    )} to ${formatDateDisplay(billDateRange.to)}`
                  : billDateRange.from
                  ? `From ${formatDateDisplay(billDateRange.from)}`
                  : billDateRange.to
                  ? `To ${formatDateDisplay(billDateRange.to)}`
                  : ""
              }
              placeholder="Select Bill Date Range"
              onClick={(e) => {
                setBillDateMenuAnchor(e.currentTarget);
                setTempBillDateRange(billDateRange);
              }}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                flex: "1 1 100px",
                cursor: "pointer",
                "& .MuiInputBase-root": {
                  height: 35,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.65rem",
                },
                "& .MuiInputBase-input": {
                  cursor: "pointer",
                },
              }}
            />
          </Grid>

          <Menu
            anchorEl={billDateMenuAnchor}
            open={Boolean(billDateMenuAnchor)}
            onClose={handleCancelBillDateRange}
            PaperProps={{
              sx: {
                width: 320,
                p: 0,
              },
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: "#fafafa",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
              >
                Select Bill Date Range
              </Typography>

              {/* From and To Date Inputs */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={tempBillDateRange.from}
                  onChange={(e) =>
                    setTempBillDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max:
                      tempBillDateRange.to ||
                      new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />

                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={tempBillDateRange.to}
                  onChange={(e) =>
                    setTempBillDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempBillDateRange.from || undefined,
                    max: new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>

              {/* Selected Range Preview */}
              {tempBillDateRange.from && tempBillDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 2,
                    p: 1,
                    bgcolor: "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "#2e7d32",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempBillDateRange.from)} to{" "}
                  {formatDateDisplay(tempBillDateRange.to)}
                </Typography>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "space-between",
                }}
              >
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  onClick={handleClearBillDateRange}
                  disabled={!billDateRange.from && !billDateRange.to}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                  }}
                >
                  Clear
                </Button>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelBillDateRange}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyBillDateRange}
                    disabled={!tempBillDateRange.from || !tempBillDateRange.to}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Menu>

          {/* Reset Button - Mobile: 50% width, Desktop: auto */}
          <Grid item xs={6} sm={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              fullWidth
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              Reset
            </Button>
          </Grid>

          {/* Add Journal Button - Mobile: 50% width, Desktop: auto */}
          <Grid item xs={6} sm={1}>
            <Button
              variant="contained"
              onClick={() => navigate("/dashboard/general-voucher-create")}
              fullWidth
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
              }}
            >
              <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Journal
            </Button>
          </Grid>
          <Grid item xs={6} sm={1}>
            <Button
              variant="contained"
              color="success"
              onClick={handleExportGeneralVoucher}
              disabled={isExcelLoading || generalVouchers.length === 0}
              fullWidth
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              {isExcelLoading ? (
                <CircularProgress size={16} color="inherit" sx={{ mr: 0.5 }} />
              ) : (
                <FileIcon fontSize="small" />
              )}
            </Button>
          </Grid>
        </Grid>{" "}
      </Box>

      {/* Date Range Menu - UPDATED */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 500,
            "& .MuiTypography-root": {
              fontSize: "0.7rem",
            },
          },
        }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setFilters((prev) => ({ ...prev, date_range: range.key }));
              setCustomDateRange({ from: "", to: "" });
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* ✅ Custom Date Range - Expandable */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}
          >
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        {/* ✅ Both Date Pickers Together */}
        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Select Date Range
            </Typography>

            {/* From and To Date Inputs Side by Side */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max:
                    tempCustomDateRange.to ||
                    new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {/* Selected Range Preview */}
            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            {/* Apply and Cancel Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      <Box sx={{ position: "relative", flex: 1, minHeight: "400px" }}>
  
  {/* ✅ LOADING OVERLAY - Only shows for filter changes */}
  {filterLoading && (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        zIndex: 1000,
        borderRadius: 1,
      }}
    >
      <CircularProgress size={50} thickness={4} color="primary" />
      <Typography sx={{ mt: 2, color: "#333", fontWeight: 500 }}>
        {filters.date_range === "financial_year"
          ? "Loading Financial Year Data..."
          : filters.date_range === "current_year"
          ? "Loading Current Year Data..."
          : filters.date_range === "this_month"
          ? "Loading This Month Data..."
          : filters.date_range === "this_week"
          ? "Loading This Week Data..."
          : filters.date_range === "previous_week"
          ? "Loading Previous Week Data..."
          : filters.date_range === "custom"
          ? "Loading Custom Date Range..."
          : filters.bill_date_from && filters.bill_date_to
          ? "Loading Bill Date Range..."
          : "Loading Data..."}
      </Typography>
      <Typography sx={{ mt: 0.5, color: "#666", fontSize: "0.8rem" }}>
        Please wait, this may take a moment
      </Typography>
    </Box>
  )}


      <InfiniteScroll
        dataLength={generalVouchers.length}
        next={fetchMoreData}
        hasMore={hasMore}
        height={isMobile ? "70vh" : "88vh"} 
        loader={
          <Typography textAlign="center" p={2}>
            Loading...
          </Typography>
        } 
        endMessage={
          <Typography textAlign="center" p={2}>
            No more vouchers
          </Typography>
        } // ✅ CHANGED
      >
        {/* ✅ ADD CONDITIONAL RENDERING */}
        {isMobile ? (
          // Mobile View
          <Box sx={{ bgcolor: "background.paper" }}>
            {generalVouchers.map((voucher, index) => (
              <MobileVoucherCard
                key={voucher.gen_voucher_no}
                voucher={voucher}
                index={index}
              />
            ))}

            {/* Totals Card for Mobile */}
            <Box
              sx={{
                p: 2,
                bgcolor: "#e3f2fd",
                borderTop: "2px solid #1976d2",
                position: "sticky",
                bottom: 0,
                zIndex: 10,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                Totals:
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">Total Amount:</Typography>
                <Typography variant="body2" fontWeight="600">
                  ₹{" "}
                  {generalVouchers
                    .reduce((sum, v) => sum + parseFloat(v.grandtotal || 0), 0)
                    .toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">Paid Amount:</Typography>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color="success.main"
                >
                  ₹{" "}
                  {generalVouchers
                    .reduce(
                      (sum, v) => sum + parseFloat(v.receipt_amount || 0),
                      0
                    )
                    .toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Balance:</Typography>
                <Typography variant="body2" fontWeight="600" color="error.main">
                  ₹{" "}
                  {generalVouchers
                    .reduce(
                      (sum, v) => sum + parseFloat(v.balance_amount || 0),
                      0
                    )
                    .toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
             sx={{ overflow: "unset" }} 
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1600, 
                "& td, & th": {
                  fontWeight: "400",
                  borderBottom: "1px solid #ccc",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  backgroundColor: "#f5f5f5",
                  padding: "0px 16px",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold !important",
                      fontSize: "0.75rem",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      whiteSpace: "nowrap",
                      pt: 0.5,
                      pb: 0.5,
                    },
                  }}
                >
                  {userRole != "Staff" && (
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                        paddingLeft: "30px",
                      }}
                    >
                      Approve
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: userRole != "Staff" ? 80 : 0, // move it right after Approve
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    S.No.
                  </TableCell>

                  <TableCell>Voucher No</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ladger Name</TableCell>
                  <TableCell>Bill No</TableCell>
                  <TableCell>Bill Date</TableCell>
                  <TableCell>Total Amt</TableCell>
                  <TableCell>Paid Amt</TableCell>
                  <TableCell>Bal Amt</TableCell>
                  <TableCell>Division</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Payment Status</TableCell>
                  <TableCell>Approved By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Narration</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell
                    className="no-print"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                      width: 200,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {generalVouchers.map((voucher, index) => (
                  <TableRow
                    hover
                    key={index}
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,

                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff", // alternate row color
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        "& td": {
                          backgroundColor: "#e3f2fd !important",
                        },
                      },
                      "& td": {
                        paddingRight: "13px !important",
                        paddingLeft: "13px !important",
                        padding: "0px",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        whiteSpace: "nowrap",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                      },
                    }}
                  >
                    {userRole != "Staff" && (
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#fafafa",
                          zIndex: 3,
                        }}
                      >
                        <Checkbox
                          checked={
                            voucher.status === "Approved" ||
                            voucher.status === "Rejected"
                          }
                          onChange={(e) => {
                            setApproveAnchorEl(e.currentTarget);
                            setGeneralToApprove(voucher);
                          }}
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                            transform: "scale(0.6)", // 👈 size kam kar diya
                            color:
                              voucher.status === "Approved"
                                ? "green"
                                : voucher.status === "Rejected"
                                ? "red"
                                : "grey",
                            "&.Mui-checked": {
                              color:
                                voucher.status === "Approved" ? "green" : "red",
                            },
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: userRole != "Staff" ? 80 : 0, // 👈 exactly the width of the first column
                        backgroundColor: "#fafafa",
                        zIndex: 3,
                      }}
                    >
                      {index + 1}
                    </TableCell>

                    <TableCell> {voucher.gen_number}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          voucher.status === "Approved"
                            ? "Approved"
                            : voucher.status === "Rejected"
                            ? "Rejected"
                            : "Pending"
                        }
                        color={
                          voucher.status === "Approved"
                            ? "success" // green
                            : voucher.status === "Rejected"
                            ? "error" // red
                            : "warning" // yellow/orange
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          // fontSize: "0.2rem", // 👈 font chhota hoga
                          height: "10px",
                          transform: "scale(0.8)",
                          "& .MuiChip-label": {
                            padding: "0 6px",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {voucher.imprest_name ||
                        voucher.supplier_name ||
                        voucher.advanceworker_name ||
                        voucher.Transporter_name}
                    </TableCell>
                    <TableCell>{voucher.bill_no}</TableCell>
                    <TableCell>{formatDate(voucher.bill_date)}</TableCell>
                    <TableCell>
                      {Number(voucher.grandtotal || 0).toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell>
                      {" "}
                      {Number(voucher.receipt_amount || 0).toLocaleString(
                        "en-IN"
                      )}
                    </TableCell>
                    <TableCell>
                      {" "}
                      {Number(voucher.balance_amount || 0).toLocaleString(
                        "en-IN"
                      )}
                    </TableCell>
                    <TableCell>{voucher.division_name}</TableCell>
                    <TableCell>{voucher.project_name || ""}</TableCell>
                    <TableCell>{voucher.created_by_name}</TableCell>

                    <TableCell>
                      <Chip
                        label={
                          voucher.payment_status === "Paid"
                            ? "Paid"
                            : voucher.payment_status === "Unpaid"
                            ? "Unpaid"
                            : "Partial"
                        }
                        color={
                          voucher.payment_status === "Paid"
                            ? "success" // green
                            : voucher.payment_status === "Unpaid"
                            ? "error" // red
                            : "warning" // yellow/orange
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          // fontSize: "0.2rem", // 👈 font chhota hoga
                          height: "10px",
                          transform: "scale(0.8)",
                          "& .MuiChip-label": {
                            padding: "0 6px",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>{voucher.approved_name}</TableCell>
                    <TableCell>{formatDate(voucher.created_at)}</TableCell>
                    {/* <TableCell>{voucher.grandtotal}</TableCell> */}

                    <TableCell>
                      <Tooltip title={voucher.narration} arrow>
                        <span>{truncateText(voucher.narration, 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={voucher.remarks || ""} arrow>
                        <span>{truncateText(voucher.remarks || "", 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#fafafa",
                        zIndex: 2,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: 36,
                          textAlign: "center",
                        }}
                      >
                        {typeof voucher.attachment === "string" ? (
                          <a
                            href={`${voucher.attachment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              color="info"
                              sx={{
                                padding: 0,
                                paddingLeft: 1,
                                paddingRight: 1,
                              }}
                              size="small"
                            >
                              <Visibility transform="scale(0.6)" />
                            </IconButton>
                          </a>
                        ) : voucher.attachment instanceof File ? (
                          // voucher.attachment.name
                          <a
                            href={URL.createObjectURL(voucher.attachment)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              color="info"
                              sx={{
                                padding: 0,
                                paddingLeft: 1,
                                paddingRight: 1,
                              }}
                              size="small"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </a>
                        ) : (
                          <span
                            style={{ display: "inline-block", width: 24 }}
                          />
                        )}
                      </span>
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        color="primary"
                        onClick={() => handleEditVoucher(voucher, index)}
                        disabled={voucher.status === "Approved"}
                        title={
                          voucher.status === "Approved"
                            ? "Cannot edit approved voucher"
                            : "Edit voucher"
                        }
                      >
                        <Edit transform="scale(0.6)" />
                      </IconButton>

                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        color="error"
                        size="small"
                        onClick={(event) => {
                          setDeleteAnchorEl(event.currentTarget);
                          setSelectedVoucherId(voucher.gen_voucher_no);
                        }}
                        disabled={voucher.status === "Approved"}
                      >
                        <Delete transform="scale(0.6)" />
                      </IconButton>

                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        color="warning"
                        size="small"
                        onClick={() => generatePdf(voucher)}
                      >
                        <Print transform="scale(0.6)" />
                      </IconButton>
                      {/* View Button */}
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow
                  sx={{
                    "& td": {
                      backgroundColor: "#E3F2FD !important", // 👈 row ke andar sab
                      color: "#000 !important",
                      fontWeight: "bold !important",
                      pt: 0.6,
                      pb: 0.6,
                    },
                  }}
                >
                  <TableCell
                    colSpan={7}
                    align="right"
                    sx={{ fontWeight: "bold !important" }}
                  >
                    Totals:
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold !important" }}>
                    {generalVouchers
                      .reduce(
                        (sum, v) => sum + parseFloat(v.grandtotal || 0),
                        0
                      )
                      .toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold !important" }}>
                    {generalVouchers
                      .reduce(
                        (sum, v) => sum + parseFloat(v.receipt_amount || 0),
                        0
                      )
                      .toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold !important" }}>
                    {generalVouchers
                      .reduce(
                        (sum, v) => sum + parseFloat(v.balance_amount || 0),
                        0
                      )
                      .toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </TableCell>
                  <TableCell colSpan={9}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>
      </Box>

 {/* Updated Popover with Fixed Mobile Table */}
<Popover
  open={Boolean(approveAnchorEl)}
  onClose={() => {
    setApproveAnchorEl(null);
    setGeneralToApprove(null);
    setRemarkInput("");
    setShowRemarkForGeneralId(null);
    setActionType(null);
  }}
  anchorReference="anchorPosition"
  anchorPosition={{
    top: window.innerHeight / 2,
    left: window.innerWidth / 2,
  }}
  transformOrigin={{
    vertical: "center",
    horizontal: "center",
  }}
  PaperProps={{
    sx: {
      maxWidth: { xs: "95vw", sm: "90vw", md: "85vw", lg: 1050 },
      maxHeight: "90vh",
      overflow: "auto",
    },
  }}
>
  <Box
    sx={{
      width: { xs: "100%", sm: "100%", md: "100%", lg: 1050 },
      minWidth: { xs: 300, sm: 500, md: 700, lg: 1050 },
      p: { xs: 1, sm: 2 },
      borderRadius: 1,
    }}
  >
    <Typography
      variant={isMobile ? "subtitle2" : "subtitle1"}
      sx={{ textAlign: "center", fontWeight: 600, mb: 2 }}
    >
      Journal Voucher
    </Typography>

    {/* Header Info - Responsive Grid/Stack Layout */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "1fr 1fr",
          md: "1fr 1fr",
        },
        gap: { xs: 1, sm: 2 },
        mb: 2,
        p: { xs: 1, sm: 2 },
        backgroundColor: "#fff",
        borderRadius: 1,
      }}
    >
      {/* ... existing header info fields remain same ... */}
      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
          Voucher No.:
        </Typography>
        <Typography variant="body2">
          {GeneralToApprove?.gen_number || "-"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
          Voucher Date:
        </Typography>
        <Typography variant="body2">
          {GeneralToApprove?.created_at
            ? dayjs(GeneralToApprove.created_at).format("DD/MM/YYYY")
            : "-"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
          Project:
        </Typography>
        <Typography variant="body2">
          {GeneralToApprove?.project_name || "-"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
          Payment Status:
        </Typography>
        <Typography variant="body2">
          {GeneralToApprove?.payment_status || "-"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
          Bill No:
        </Typography>
        <Typography variant="body2">
          {GeneralToApprove?.bill_no || "-"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
          Bill Date:
        </Typography>
        <Typography variant="body2">
          {GeneralToApprove?.bill_date
            ? dayjs(GeneralToApprove.bill_date).format("DD/MM/YYYY")
            : "-"}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gridColumn: { xs: "1", sm: "1 / -1" },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
          Ledger Name:
        </Typography>
        <Typography variant="body2">
          {GeneralToApprove?.supplier_name ||
            GeneralToApprove?.imprest_name ||
            GeneralToApprove?.advanceworker_name ||
            GeneralToApprove?.Transporter_name ||
            GeneralToApprove?.party_name ||
            GeneralToApprove?.expense_name ||
            "-"}
        </Typography>
      </Box>
    </Box>

    <Divider sx={{ my: 2 }} />
    {isMobile ? (
      // 🎯 Mobile Card View - No Scrolling
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            mb: 1,
            p: 1,
            bgcolor: "#f5f5f5",
            borderRadius: 1,
          }}
        >
          Expense Details
        </Typography>

        {GeneralToApprove?.sub_vouchers?.map((item, index) => (
          <Box
            key={item.id || index}
            sx={{
              mb: 1.5,
              p: 1.5,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              bgcolor: index % 2 === 0 ? "#fafafa" : "#fff",
            }}
          >
            {/* Expense Name - Header */}
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.85rem",
                color: "#1976d2",
                mb: 1,
                pb: 0.5,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              {index + 1}. {item.expense_name || "-"}
            </Typography>

            {/* Details Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.8,
              }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: "0.65rem", color: "#666", mb: 0.2 }}
                >
                  Cost Type:
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                  {item.cost_type || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.65rem", color: "#666", mb: 0.2 }}
                >
                  Amount:
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                  ₹{item.amount || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.65rem", color: "#666", mb: 0.2 }}
                >
                  GST:
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                  ₹{item.gst || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.65rem", color: "#666", mb: 0.2 }}
                >
                  TDS:
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                  ₹{item.tds || "-"}
                </Typography>
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography
                  sx={{ fontSize: "0.65rem", color: "#666", mb: 0.2 }}
                >
                  Total Amount:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "#2e7d32",
                  }}
                >
                  ₹{item.total_amount || "-"}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}

        {/* Mobile Totals Card */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: "#e3f2fd",
            borderRadius: 1,
            border: "2px solid #1976d2",
          }}
        >
          <Typography
            sx={{ fontWeight: 600, fontSize: "0.85rem", mb: 1 }}
          >
            Totals
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0.8,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                Amount:
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                ₹
                {GeneralToApprove?.sub_vouchers
                  ?.reduce(
                    (sum, item) => sum + parseFloat(item.amount || 0),
                    0
                  )
                  .toFixed(2)}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                GST:
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                ₹
                {GeneralToApprove?.sub_vouchers
                  ?.reduce((sum, item) => sum + parseFloat(item.gst || 0), 0)
                  .toFixed(2)}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                TDS:
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                ₹
                {GeneralToApprove?.sub_vouchers
                  ?.reduce((sum, item) => sum + parseFloat(item.tds || 0), 0)
                  .toFixed(2)}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                Total:
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "#2e7d32",
                }}
              >
                ₹
                {GeneralToApprove?.sub_vouchers
                  ?.reduce(
                    (sum, item) => sum + parseFloat(item.total_amount || 0),
                    0
                  )
                  .toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    ) : (
      // 🖥️ Desktop Table View - With Horizontal Scroll
      <Box
        sx={{
          overflowX: "auto",
          width: "100%",
          "&::-webkit-scrollbar": {
            height: 6,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 3,
          },
        }}
      >
        <Table
          aria-label="basic table"
          sx={{
            minWidth: 700,
          }}
        >
          <thead>
            <tr>
              {[
                "Expenses",
                "Cost Type",
                "Amount",
                "GST",
                "TDS",
                "Total Amount",
              ].map((head) => (
                <th
                  key={head}
                  style={{
                    background: "#f5f5f5",
                    fontWeight: 600,
                    textAlign: "center",
                    borderBottom: "1px solid #e5e7eb",
                    padding: "8px 4px",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {GeneralToApprove?.sub_vouchers?.map((item, index) => (
              <tr
                key={item.id || index}
                style={{
                  background: "#fff",
                  textAlign: "center",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <td style={{ padding: "8px 4px", fontSize: "14px" }}>
                  {item.expense_name || "-"}
                </td>
                <td style={{ padding: "8px 4px", fontSize: "14px" }}>
                  {item.cost_type || "-"}
                </td>
                <td style={{ padding: "8px 4px", fontSize: "14px" }}>
                  {item.amount || "-"}
                </td>
                <td style={{ padding: "8px 4px", fontSize: "14px" }}>
                  {item.gst || "-"}
                </td>
                <td style={{ padding: "8px 4px", fontSize: "14px" }}>
                  {item.tds || "-"}
                </td>
                <td style={{ padding: "8px 4px", fontSize: "14px" }}>
                  {item.total_amount || "-"}
                </td>
              </tr>
            ))}

            {/* Total Row */}
            <tr
              style={{
                background: "#f4f6f8",
                textAlign: "center",
                borderBottom: "1px solid #e5e7eb",
                fontWeight: 600,
              }}
            >
              <td
                colSpan={2}
                style={{
                  textAlign: "right",
                  padding: "12px 8px",
                  fontSize: "14px",
                }}
              >
                Total
              </td>
              <td style={{ padding: "12px 8px", fontSize: "14px" }}>
                {GeneralToApprove?.sub_vouchers
                  ?.reduce(
                    (sum, item) => sum + parseFloat(item.amount || 0),
                    0
                  )
                  .toFixed(2)}
              </td>
              <td style={{ padding: "12px 8px", fontSize: "14px" }}>
                {GeneralToApprove?.sub_vouchers
                  ?.reduce((sum, item) => sum + parseFloat(item.gst || 0), 0)
                  .toFixed(2)}
              </td>
              <td style={{ padding: "12px 8px", fontSize: "14px" }}>
                {GeneralToApprove?.sub_vouchers
                  ?.reduce((sum, item) => sum + parseFloat(item.tds || 0), 0)
                  .toFixed(2)}
              </td>
              <td style={{ padding: "12px 8px", fontSize: "14px" }}>
                {GeneralToApprove?.sub_vouchers
                  ?.reduce(
                    (sum, item) => sum + parseFloat(item.total_amount || 0),
                    0
                  )
                  .toFixed(2)}
              </td>
            </tr>
          </tbody>
        </Table>
      </Box>
    )}

    {/* Remark Section - Same as before */}
    {showRemarkForGeneralId === GeneralToApprove?.gen_voucher_no ? (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ textAlign: "left", mb: 1 }}>
          Please enter remark before{" "}
          {actionType === "Approved" ? "approving" : "rejecting"}:
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={remarkInput}
          onChange={(e) => setRemarkInput(e.target.value)}
          placeholder="Enter remark"
          multiline={isMobile}
          rows={isMobile ? 2 : 1}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            color={actionType === "Approved" ? "success" : "error"}
            size="small"
            onClick={() => {
              if (!remarkInput.trim()) {
                toast.error("Remark is required!");
                return;
              }
              handleApprove({
                ...GeneralToApprove,
                status: actionType,
                remarks: remarkInput,
              });
              setApproveAnchorEl(null);
              setGeneralToApprove(null);
              setRemarkInput("");
              setShowRemarkForGeneralId(null);
              setActionType(null);
            }}
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setApproveAnchorEl(null);
              setGeneralToApprove(null);
              setRemarkInput("");
              setShowRemarkForGeneralId(null);
              setActionType(null);
            }}
          >
            No
          </Button>
        </Box>
      </Box>
    ) : (
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          gap: 1,
          mt: 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          color="success"
          size="small"
          onClick={() => {
            setShowRemarkForGeneralId(GeneralToApprove?.gen_voucher_no);
            setActionType("Approved");
          }}
          disabled={GeneralToApprove?.receipt_amount != 0.0}
          sx={{ minWidth: { xs: "45%", sm: "auto" } }}
        >
          Approve
        </Button>

        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={() => {
            setShowRemarkForGeneralId(GeneralToApprove?.gen_voucher_no);
            setActionType("Rejected");
          }}
          disabled={GeneralToApprove?.receipt_amount != 0.0}
          sx={{ minWidth: { xs: "45%", sm: "auto" } }}
        >
          Reject
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={() => handleEditVoucher(GeneralToApprove)}
          disabled={GeneralToApprove?.status === "Approved"}
          sx={{
            minWidth: { xs: "45%", sm: "auto" },
            color: "#322aceff",
            borderColor: "#322aceff",
          }}
        >
          Edit
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setApproveAnchorEl(null);
            setGeneralToApprove(null);
            setRemarkInput("");
            setShowRemarkForGeneralId(null);
            setActionType(null);
          }}
          sx={{
            minWidth: { xs: "45%", sm: "auto" },
            color: "#95A5A6",
            borderColor: "#95A5A6",
          }}
        >
          Close
        </Button>
      </Box>
    )}
  </Box>
</Popover>

    {/* Delete Confirmation Popover - Already Responsive */}
    <Popover
      open={Boolean(deleteAnchorEl)}
      anchorEl={deleteAnchorEl}
      onClose={() => setDeleteAnchorEl(null)}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          maxWidth: { xs: "90vw", sm: 250 },
        },
      }}
    >
      <Box sx={{ p: 2, maxWidth: { xs: "100%", sm: 200 } }}>
        <Typography variant="body2">Are you sure you want?</Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDeleteAnchorEl(null)}
          >
            No
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => {
              handleDeleteVoucher(selectedVoucherId);
              setDeleteAnchorEl(null);
            }}
          >
            Yes
          </Button>
        </Box>
      </Box>
    </Popover>
    </>
  );
};

export default GeneralVoucher;
